import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { send } from '@/shared/messages';
import { DEFAULT_SETTINGS, MODELS, THINKING_OPTIONS, type ModelId } from '@/shared/constants';
import { TestsManager } from '@/components/TestsManager';
import { ChaptersManager } from '@/components/ChaptersManager';
import { ThemeControl } from '@/components/ThemeToggle';
import { InvitePanel } from './InvitePanel';
import { PortableLinkPanel } from './PortableLinkPanel';
import { LoginCodePanel } from './LoginCodePanel';
import { getMe } from './auth';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Field';

// Webapp settings page — extension-only sections (API key, hosts, shim
// grant) are intentionally omitted because the server holds the
// Anthropic key and the host-permission concept doesn't apply outside
// the Chrome extension.
export function Settings() {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    void getMe().then((me) => setIsAdmin(!!me?.is_admin));
  }, []);

  return (
    <div className="@container mx-auto max-w-3xl space-y-6 p-4 @md:p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <Link to="/" className="text-sm text-blue-600 hover:underline">
          ← back
        </Link>
      </header>
      <AppearanceSection />
      <StudySettingsSection />
      <TestsManager />
      <ChaptersManager />
      <PortableLinkPanel />
      <LoginCodePanel />
      {isAdmin && <InvitePanel />}
      <LibrarySection />
      <UsageSection />
    </div>
  );
}

function AppearanceSection() {
  return (
    <Card className="flex items-center justify-between gap-2">
      <div>
        <h2 className="text-lg font-semibold">Appearance</h2>
        <p className="text-xs text-slate-600">Theme — System follows your device.</p>
      </div>
      <ThemeControl />
    </Card>
  );
}

function StudySettingsSection() {
  const [s, setS] = useState(DEFAULT_SETTINGS);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    void send({ type: 'GET_SETTINGS' }).then((r) => {
      setS({ ...DEFAULT_SETTINGS, ...(r.settings as Partial<typeof DEFAULT_SETTINGS>) });
    });
  }, []);

  async function save<K extends keyof typeof DEFAULT_SETTINGS>(
    key: K,
    val: (typeof DEFAULT_SETTINGS)[K],
  ) {
    setS((prev) => ({ ...prev, [key]: val }));
    await send({ type: 'SET_SETTINGS', partial: { [key]: val } });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 800);
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">Study settings</h2>
        {savedFlash && <span className="text-xs text-green-700">saved</span>}
      </div>
      <div className="grid grid-cols-1 gap-3 text-sm @md:grid-cols-2">
        <Field label="New cards / day">
          <Input
            type="number"
            min={0}
            max={100}
            value={s.dailyNewCardCap}
            onChange={(e) => void save('dailyNewCardCap', Number(e.target.value))}
            className="w-20"
          />
        </Field>
        <Field label="Reviews / day">
          <Input
            type="number"
            min={0}
            max={500}
            value={s.dailyReviewCardCap}
            onChange={(e) => void save('dailyReviewCardCap', Number(e.target.value))}
            className="w-20"
          />
        </Field>
        <Field label="Daily session goal (min)">
          <Input
            type="number"
            min={5}
            max={480}
            value={s.dailySessionGoalMin}
            onChange={(e) => void save('dailySessionGoalMin', Number(e.target.value))}
            className="w-20"
          />
        </Field>
      </div>
      <div className="space-y-2 border-t border-slate-200 pt-3 text-sm">
        <h3 className="text-sm font-medium">Model assignments</h3>
        <Field label="Ingest (chapter → cards)">
          <ModelSelect
            value={s.taskModels.ingest}
            onChange={(m) => void save('taskModels', { ...s.taskModels, ingest: m })}
          />
        </Field>
        <Field label="Q&A">
          <ModelSelect
            value={s.taskModels.qa}
            onChange={(m) => void save('taskModels', { ...s.taskModels, qa: m })}
          />
        </Field>
        <Field label="Thinking (chat & Q&A)">
          <ThinkingSelect
            value={s.thinkingBudget}
            onChange={(v) => void save('thinkingBudget', v)}
          />
        </Field>
        <Field label="Chat history (turns replayed per question; -1 = all)">
          <Input
            type="number"
            min={-1}
            step={1}
            value={s.chatHistoryTurns ?? DEFAULT_SETTINGS.chatHistoryTurns}
            onChange={(e) =>
              void save('chatHistoryTurns', Math.max(-1, Math.floor(Number(e.target.value) || 0)))
            }
            className="w-20 text-right"
          />
        </Field>
      </div>
      <div className="space-y-1 border-t border-slate-200 pt-3 text-sm">
        <Field label="Topics that resonate (used to seed analogies/mnemonics)">
          <Input
            type="text"
            value={s.userInterests}
            onChange={(e) => void save('userInterests', e.target.value)}
            placeholder="e.g. cooking, sailing, video games"
            className="w-full"
          />
        </Field>
      </div>
    </Card>
  );
}

function LibrarySection() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<string | null>(null);
  const [importInfo, setImportInfo] = useState<string | null>(null);

  async function doExport() {
    setExporting(true);
    setError(null);
    setExportInfo(null);
    try {
      const r = await send({ type: 'EXPORT_LIBRARY' });
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = `tishnotes-library-${ts}.json`;
      const blob = new Blob([r.payloadJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportInfo(`Exported · ${(r.bytes / 1024).toFixed(1)} KB`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  async function doImport(file: File) {
    setError(null);
    setImportInfo(null);
    let text: string;
    try {
      text = await file.text();
    } catch (e) {
      setError(`Could not read file: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    if (
      !confirm(
        `Replace your library with the contents of "${file.name}"?\n\n` +
          `Wipes existing chapters, sources, summaries, flashcards, Q&A, ` +
          `and review logs for your account. Chats, settings, and usage stats ` +
          `are kept. This cannot be undone.`,
      )
    ) {
      return;
    }
    setImporting(true);
    try {
      const r = await send({
        type: 'IMPORT_LIBRARY',
        payloadJson: text,
        replace: true,
        resetFsrs: true,
      });
      setImportInfo(
        `Imported ${r.added.chapters} chapters · ${r.added.sources} sources · ` +
          `${r.added.summaries} summaries · ${r.added.flashcards} cards.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  }

  return (
    <Card className="space-y-3">
      <h2 className="text-lg font-semibold">Library export / import</h2>
      <p className="text-xs text-slate-600">
        Export downloads a JSON snapshot of your chapters, sources, summaries, and
        flashcards. Import accepts that same JSON shape (also produced by the Chrome
        extension) and replaces the corpus on your account.
      </p>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Button variant="primary" onClick={() => void doExport()} loading={exporting}>
          {exporting ? 'Exporting…' : 'Export library'}
        </Button>
        <label className="flex items-center gap-2 rounded border border-slate-300 bg-card px-3 py-1.5 hover:bg-slate-50">
          <span className="font-medium text-slate-700">
            {importing ? 'Importing…' : 'Import library…'}
          </span>
          <input
            type="file"
            accept="application/json,.json"
            disabled={importing}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void doImport(f);
              e.currentTarget.value = '';
            }}
            className="hidden"
          />
        </label>
      </div>
      <p className="text-[11px] text-slate-500">
        Import <strong>replaces</strong> your library. Run an export first if you
        have anything you don't want to lose.
      </p>
      {exportInfo && <Alert tone="success">✓ {exportInfo}</Alert>}
      {importInfo && <Alert tone="success">✓ {importInfo}</Alert>}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}

function UsageSection() {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof loadStats>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setStats(await loadStats());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Token usage & cost</h2>
        <Button size="xs" onClick={() => void refresh()}>
          Refresh
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
      {!stats && !error && <div className="text-xs text-slate-500">Loading…</div>}
      {stats && (
        <div className="grid grid-cols-1 gap-2 text-xs @xl:grid-cols-3">
          <Bucket title="Today" bucket={stats.today} />
          <Bucket title="Last 7 days" bucket={stats.last7d} />
          <Bucket title="All time" bucket={stats.total} highlight />
        </div>
      )}
    </Card>
  );
}

async function loadStats() {
  return await send({ type: 'GET_API_USAGE_STATS' });
}

function Bucket({
  title,
  bucket,
  highlight,
}: {
  title: string;
  bucket: { costUsd: number; callCount: number; inputTokens: number; outputTokens: number };
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded border p-3 ${
        highlight ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">
        ${bucket.costUsd.toFixed(3)}
      </div>
      <div className="text-[10px] text-slate-600">
        {bucket.callCount.toLocaleString()} call{bucket.callCount === 1 ? '' : 's'} ·{' '}
        in {fmt(bucket.inputTokens)} · out {fmt(bucket.outputTokens)}
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function ModelSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: ModelId) => void;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value as ModelId)}>
      <option value={MODELS.haiku}>Haiku 4.5 (cheap)</option>
      <option value={MODELS.sonnet}>Sonnet 4.6 (balanced)</option>
      <option value={MODELS.opus}>Opus 4.7 (best quality)</option>
    </Select>
  );
}

function ThinkingSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {THINKING_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3">
      <span className="text-slate-700">{label}</span>
      {children}
    </label>
  );
}
